import type {
  AccountOut,
  ChangePasswordRequest,
  LoginRequest,
  PasswordResetConfirmRequest,
  PasswordResetRequest,
  ReconcilePreview,
  ReconcileResult,
  SignupRequest,
} from "@/types";
import { apiDelete, apiGet, apiPost } from "./client";

/**
 * Endpoints d'authentification par compte (E1 lot 1 — S11 backend).
 * Cookie httpOnly `sunmatch_session` (JWT HS256) posé par `signup`/`login`.
 * Voir `docs/api/endpoints.md` § 2.bis.
 *
 * Tous les appels héritent de `withCredentials: true` (cf. `apiClient`) :
 * le cookie est posé/transmis automatiquement, jamais de token en
 * `localStorage` (cf. ADR-002 et garde-fous frontend).
 */

const BASE = "/api/v1/auth";

/**
 * Crée un compte puis auto-login (pose le cookie `sunmatch_session`).
 * Erreurs : 409 `EMAIL_ALREADY_EXISTS`, 422 (email invalide / mdp < 10 /
 * confirmation ≠ mdp / `consent` ≠ true).
 */
export function signup(body: SignupRequest): Promise<AccountOut> {
  return apiPost<AccountOut, SignupRequest>(`${BASE}/signup`, body);
}

/**
 * Authentifie un compte et pose le cookie `sunmatch_session`.
 * Erreur : 401 `INVALID_CREDENTIALS` (corps générique, anti-énumération).
 */
export function login(body: LoginRequest): Promise<AccountOut> {
  return apiPost<AccountOut, LoginRequest>(`${BASE}/login`, body);
}

/**
 * Profil du compte authentifié. 401 `AUTH_REQUIRED` si non connecté.
 *
 * Endpoint **non lenient** : contrairement à `/sessions/me`, il renvoie un
 * 401 quand aucun compte n'est connecté. Les hooks consumers traitent ce 401
 * comme « pas connecté » (état nominal), sans le propager comme une erreur.
 */
export function getAccount(): Promise<AccountOut> {
  return apiGet<AccountOut>(`${BASE}/me`);
}

/**
 * Déconnecte : efface le cookie `sunmatch_session` (Max-Age=0). Lenient
 * côté backend (jamais 401, idempotent). Ne touche pas `sunmatch_anon`.
 */
export function logout(): Promise<void> {
  return apiPost<void>(`${BASE}/logout`);
}

/**
 * « Déconnecter tous les appareils » (S15-2). Incrémente `token_version`
 * côté serveur → invalide **tous** les JWT du compte : cet appareil **et**
 * les autres (plus un éventuel token volé). Le backend efface aussi le cookie
 * `sunmatch_session` courant — on est donc déconnecté ici également. Ne touche
 * **jamais** `sunmatch_anon`. Succès = 204 sans body. 401 `AUTH_REQUIRED` si
 * non authentifié. À distinguer de `logout` (appareil courant uniquement).
 */
export function logoutAll(): Promise<void> {
  return apiPost<void>(`${BASE}/logout-all`);
}

/**
 * Supprime définitivement le **compte** authentifié et toutes ses données en
 * cascade — sites, simulations, consommations, productions, jobs d'import,
 * tokens Enedis (RGPD art. 17, droit à l'effacement, cf. `DELETE /auth/me`,
 * S11-6). Le backend purge en une transaction SQL puis efface le cookie
 * `sunmatch_session` (Max-Age=0) ; il ne touche **jamais** `sunmatch_anon`
 * (arbitrage A4). Succès = 204 sans body.
 *
 * À distinguer de `DELETE /sessions/me` (effacement de la **session anonyme**) :
 * l'aiguillage selon l'identité est porté par `useDeleteMyData` — un compte ne
 * doit JAMAIS être effacé via `/sessions/me` (ses données scopées `account_id`
 * survivraient). Erreur : 401 `AUTH_REQUIRED` si non authentifié.
 */
export function deleteMyAccount(): Promise<void> {
  return apiDelete<void>(`${BASE}/me`);
}

/**
 * Change le mot de passe du compte authentifié (E1 lot 3 — S13-2 backend, cf.
 * `docs/api/endpoints.md` § 2.bis). Le backend vérifie `current_password`
 * (argon2id), enregistre le nouveau hash, puis **ré-émet** le cookie
 * `sunmatch_session` (rotation de session — transparent ici grâce à
 * `withCredentials`). Succès = 204 sans body.
 *
 * Le body posté est strictement `{ current_password, new_password }` : la
 * confirmation de saisie est une contrainte de formulaire front (jamais
 * envoyée), comme au signup.
 *
 * Erreurs : 403 `INVALID_CURRENT_PASSWORD` (mauvais mot de passe actuel),
 * 401 `AUTH_REQUIRED` (session expirée/absente), 422 (`new_password` < 10 —
 * normalement déjà bloqué par Zod côté formulaire avant l'appel).
 */
export function changePassword(body: ChangePasswordRequest): Promise<void> {
  return apiPost<void, ChangePasswordRequest>(`${BASE}/me/password`, body);
}

/**
 * Réinitialisation du mot de passe (F2 — S35, ADR-018). Contrat figé S35-1,
 * cf. `docs/api/endpoints.md` § 2.quater. **Aucune identité requise** : ces deux
 * endpoints sont publics (l'utilisateur a justement perdu l'accès à son compte).
 */

/**
 * Demande un lien de réinitialisation. **Succès = 202 sans corps**, et
 * **identique dans les quatre cas** : compte avec mot de passe · compte
 * **Google-only** · e-mail **inconnu** · échec d'envoi amont (anti-énumération
 * **C7c**). Il n'y a donc **rien à brancher** sur la réponse : n'affichez jamais
 * autre chose que la confirmation générique (« si un compte existe… »).
 *
 * Erreurs : **429 `RATE_LIMITED`** (2 couches — 5/minute par IP, **3/hour** par
 * e-mail, anti-mail-bombing ; le corps ne révèle pas laquelle a sauté),
 * **422** si l'e-mail est syntaxiquement malformé (normalement déjà bloqué par
 * Zod côté formulaire).
 */
export function requestPasswordReset(body: PasswordResetRequest): Promise<void> {
  return apiPost<void, PasswordResetRequest>(
    `${BASE}/password-reset/request`,
    body,
  );
}

/**
 * Consomme le jeton et pose le nouveau mot de passe. Le jeton part **dans le
 * corps**, jamais en query (**C6** — le corps n'est journalisé nulle part).
 *
 * Succès = **204 sans body et SANS auto-login** (ADR-018 D6 : le lien dort dans
 * une boîte mail parfois consultée sur un appareil partagé ; en faire une
 * session instantanée étendrait son rayon d'action au-delà de sa finalité). Le
 * backend bumpe `token_version` → **toutes** les sessions antérieures deviennent
 * 401. Le call-site renvoie vers `/login`.
 *
 * Erreurs — les **trois** codes de jeton mènent au **même** message FR côté UI
 * (« Ce lien n'est plus valide. Demandez-en un nouveau. ») : l'action
 * utilisateur est la même. Ils restent distincts **pour la recette** (la CdC
 * § F.5 exige de prouver « valide une seule fois » **et** « expiré » séparément) :
 * - **400 `PASSWORD_RESET_TOKEN_INVALID`** — jeton inconnu, malformé, ou déjà
 *   balayé par la purge (au-delà de ~24 h, un lien périmé tombe ici, ADR-018 D7) ;
 * - **410 `PASSWORD_RESET_TOKEN_USED`** — jeton déjà consommé (usage unique) ;
 * - **410 `PASSWORD_RESET_TOKEN_EXPIRED`** — TTL (1 h) dépassé ;
 * - **422** — `new_password` < 10 (déjà bloqué par Zod), **429 `RATE_LIMITED`**.
 */
export function confirmPasswordReset(
  body: PasswordResetConfirmRequest,
): Promise<void> {
  return apiPost<void, PasswordResetConfirmRequest>(
    `${BASE}/password-reset/confirm`,
    body,
  );
}

/**
 * Réconciliation des sessions anonymes (E1 lot 2 — S12 backend). Contrat figé
 * spike S12-1, cf. `docs/api/endpoints.md` § 2.bis. Requièrent un compte
 * authentifié (cookie JWT `sunmatch_session`) ; lisent en plus le cookie
 * anonyme `sunmatch_anon` s'il est présent.
 */

/**
 * Prévisualise (lecture seule, aucune migration) les ressources rattachées au
 * cookie `sunmatch_anon` courant. Alimente la modale post-signup §E1.6.
 * Cookie anon absent → `{ sites_count: 0, simulations_count: 0 }`.
 * Erreur : 401 `AUTH_REQUIRED` si non authentifié.
 */
export function getReconcilePreview(): Promise<ReconcilePreview> {
  return apiGet<ReconcilePreview>(`${BASE}/reconcile/preview`);
}

/**
 * Rattache au compte authentifié **toutes** les ressources de l'`sunmatch_anon`
 * courant (sur confirmation explicite de l'utilisateur, §E1.6/E1.7). Purge le
 * cookie `sunmatch_anon` au commit. Idempotent (re-appel sans cookie → no-op).
 * Erreurs : 401 `AUTH_REQUIRED`, 409 `RECONCILE_ENEDIS_TOKEN_PRESENT` (garde-fou
 * B9 — l'anon détient un token Enedis, migration reportée au lot 4).
 */
export function postReconcile(): Promise<ReconcileResult> {
  return apiPost<ReconcileResult>(`${BASE}/reconcile`);
}
